import * as React from 'react';
import { Button, useDataProvider, useNotify } from 'react-admin';
import {
    Typography, Box, Chip, Dialog, DialogTitle, DialogContent, DialogActions,
    TextField as MuiTextField,
} from '@material-ui/core';
import { describePeriod, errorMessage, formatDate, formatDateTime, getObject, post } from './noticeApi';

const stateLabels = {
    scheduled: 'Scheduled',
    due: 'Due',
    issued: 'Notice given',
    rescinded: 'Notice rescinded',
    short_engagement: 'No notice needed',
    no_end_date: 'No end date',
    not_applicable: 'Not applicable',
    ended: 'Ended',
};

/**
 * What the state means for this employment, in a sentence.
 */
const explain = (status) => {
    const automatic = status.notices_enabled;

    switch (status.state) {
        case 'scheduled':
            return automatic
                ? `Notice will be emailed to the crew member on ${formatDate(status.due_date)}.`
                : 'Automatic notices are switched off for this production, so nothing will be sent unless you give notice by hand.';
        case 'due':
            return automatic
                ? 'Notice is due and will go out with the next daily run.'
                : 'Notice is due, but automatic notices are switched off for this production. Give it by hand if it is needed.';
        case 'rescinded':
            return 'Notice was rescinded. It will not be sent again automatically unless the end date changes.';
        case 'short_engagement':
            return 'This engagement is no longer than its notice period, so no notice is sent.';
        case 'no_end_date':
            return 'Set an end date and notice will be scheduled.';
        case 'not_applicable':
            return 'Notice only applies to crew who are active or paused.';
        case 'ended':
            return 'This employment has ended.';
        default:
            return null;
    }
};

const who = (notice) => (notice.issue_method === 'automatic' ? 'automatically' : `by ${notice.issued_by?.name ?? 'staff'}`);

/**
 * Notice of the end of an engagement, for the employment page: where it is,
 * and buttons to give it early or rescind it. Shows what is saved, so save
 * changes to the end date or notice period first.
 *
 * The props after employmentId only make the panel reload when the saved
 * employment changes.
 */
export const NoticePanel = ({ employmentId, endDate, noticePeriodDays, employmentStatus }) => {
    const dataProvider = useDataProvider();
    const notify = useNotify();
    const [status, setStatus] = React.useState(null);
    const [loading, setLoading] = React.useState(true);
    const [failed, setFailed] = React.useState(false);
    const [busy, setBusy] = React.useState(false);
    const [issueOpen, setIssueOpen] = React.useState(false);
    const [rescindOpen, setRescindOpen] = React.useState(false);
    const [reason, setReason] = React.useState('');

    React.useEffect(() => {
        let cancelled = false;
        setLoading(true);

        getObject(dataProvider, `employees/${employmentId}/notice`)
            .then(data => { if (!cancelled) { setStatus(data); setFailed(false); setLoading(false); } })
            .catch(() => { if (!cancelled) { setFailed(true); setLoading(false); } });

        return () => { cancelled = true; };
    }, [dataProvider, employmentId, endDate, noticePeriodDays, employmentStatus]);

    const run = (path, data, successMessage, close) => {
        setBusy(true);

        // create() does not return the server's response, so the new status is fetched
        post(dataProvider, `employees/${employmentId}/notice/${path}`, data)
            .then(
                () => {
                    notify(successMessage, 'success');
                    close();

                    return getObject(dataProvider, `employees/${employmentId}/notice`)
                        .then(setStatus)
                        .catch(() => notify('Saved, but the notice details could not be reloaded. Refresh the page.', 'warning'));
                },
                err => notify(errorMessage(err, 'Something went wrong'), 'error')
            )
            .finally(() => setBusy(false));
    };

    const giveNotice = () => run('issue', {}, status?.outdated ? 'Revised notice sent' : 'Notice given', () => setIssueOpen(false));
    const rescind = () => run('rescind', reason.trim() ? { reason: reason.trim() } : {}, 'Notice rescinded', () => setRescindOpen(false));

    const active = status?.active_notice;

    return (
        <Box mb={3}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                <Typography variant="h6">Notice</Typography>
            </Box>

            {loading && !status && <Typography variant="body2">Loading...</Typography>}
            {failed && !status && <Typography variant="body2" color="textSecondary">Notice details could not be loaded.</Typography>}

            {status && (
                <>
                    <Box mb={1} display="flex" alignItems="center" flexWrap="wrap" style={{ gap: 8 }}>
                        <Chip
                            size="small"
                            label={stateLabels[status.state] ?? status.state}
                            color={status.outdated ? 'secondary' : status.state === 'issued' ? 'primary' : 'default'}
                        />
                        {status.outdated && <Chip size="small" color="secondary" label="End date has changed" />}
                    </Box>

                    <Box display="flex" flexWrap="wrap" mb={1}>
                        <Box mr={3}>
                            <Typography variant="caption" color="textSecondary">Notice period</Typography>
                            <Typography variant="body2">{describePeriod(status.notice_period_days) || `${status.notice_period_days} days`}</Typography>
                        </Box>
                        <Box mr={3}>
                            <Typography variant="caption" color="textSecondary">End date</Typography>
                            <Typography variant="body2">{status.end_date ? formatDate(status.end_date) : '—'}</Typography>
                        </Box>
                        <Box mr={3}>
                            <Typography variant="caption" color="textSecondary">Notice due</Typography>
                            <Typography variant="body2">{status.due_date ? formatDate(status.due_date) : '—'}</Typography>
                        </Box>
                    </Box>

                    {active && (
                        <Typography variant="body2" gutterBottom>
                            Notice was given {who(active)} on {formatDateTime(active.issued_at)} for an end date of {formatDate(active.end_date_at_issue)}.{' '}
                            {active.email_sent_at
                                ? `Emailed to the crew member on ${formatDateTime(active.email_sent_at)}.`
                                : 'The email has not been sent yet.'}
                        </Typography>
                    )}

                    {status.outdated && (
                        <Typography variant="body2" color="error" gutterBottom>
                            The end date has changed since this notice was given, so the crew member has been told the wrong date.
                            Give a revised notice, or rescind it.
                        </Typography>
                    )}

                    {!active && explain(status) && (
                        <Typography variant="body2" color="textSecondary" gutterBottom>{explain(status)}</Typography>
                    )}

                    <Box display="flex" flexWrap="wrap" style={{ gap: 8 }}>
                        {status.can_issue && (
                            <Button
                                label={status.outdated ? 'Give revised notice' : 'Give notice now'}
                                onClick={() => setIssueOpen(true)}
                                disabled={busy}
                            />
                        )}
                        {status.can_rescind && (
                            <Button
                                label="Rescind notice"
                                onClick={() => { setReason(''); setRescindOpen(true); }}
                                disabled={busy}
                            />
                        )}
                    </Box>

                    {status.history?.length > 0 && (
                        <Box mt={2}>
                            <Typography variant="subtitle2" gutterBottom>History</Typography>
                            {status.history.map(n => (
                                <Box key={n.id} py={0.5} borderBottom="1px solid #eee">
                                    <Typography variant="body2">
                                        Notice for {formatDate(n.end_date_at_issue)} ({describePeriod(n.notice_period_days) || `${n.notice_period_days} days`})
                                        {n.rescinded_at ? ' — rescinded' : ''}
                                    </Typography>
                                    <Typography variant="caption" color="textSecondary" display="block">
                                        Given {who(n)} on {formatDateTime(n.issued_at)}
                                        {n.email_sent_at ? `, emailed ${formatDateTime(n.email_sent_at)}` : ''}
                                    </Typography>
                                    {n.rescinded_at && (
                                        <Typography variant="caption" color="textSecondary" display="block">
                                            Rescinded {formatDateTime(n.rescinded_at)}
                                            {n.rescinded_by?.name ? ` by ${n.rescinded_by.name}` : ''}
                                            {n.rescind_reason ? ` — ${n.rescind_reason}` : ''}
                                        </Typography>
                                    )}
                                </Box>
                            ))}
                        </Box>
                    )}
                </>
            )}

            <Dialog open={issueOpen} onClose={() => setIssueOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>{status?.outdated ? 'Give revised notice' : 'Give notice now'}</DialogTitle>
                <DialogContent>
                    <Typography variant="body1" gutterBottom>
                        The crew member will be emailed now, telling them their engagement ends on{' '}
                        <strong>{status?.end_date ? formatDate(status.end_date) : ''}</strong>.
                    </Typography>
                    {status?.outdated && active && (
                        <Typography variant="body2" color="textSecondary">
                            This replaces the notice given on {formatDate(active.issued_at)} for {formatDate(active.end_date_at_issue)}.
                            The email refers to that earlier notice.
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button label="Cancel" onClick={() => setIssueOpen(false)} />
                    <Button label={status?.outdated ? 'Send revised notice' : 'Give notice'} onClick={giveNotice} disabled={busy} />
                </DialogActions>
            </Dialog>

            <Dialog open={rescindOpen} onClose={() => setRescindOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>Rescind notice</DialogTitle>
                <DialogContent>
                    <Typography variant="body1" gutterBottom>
                        {active?.email_sent_at
                            ? `The crew member will be emailed to say the notice given on ${formatDate(active.issued_at)} no longer applies.`
                            : 'The notice email has not been sent yet, so the crew member will not be emailed.'}
                    </Typography>
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                        It will not be sent again automatically for this end date. The reason is kept in the activity log and is
                        not included in the email.
                    </Typography>
                    <Box mt={1}>
                        <MuiTextField
                            id="notice-rescind-reason"
                            label="Reason (optional)"
                            value={reason}
                            onChange={e => setReason(e.target.value)}
                            fullWidth
                            multiline
                            minRows={2}
                            inputProps={{ maxLength: 500 }}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button label="Back" onClick={() => setRescindOpen(false)} />
                    <Button label="Rescind notice" onClick={rescind} disabled={busy} />
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default NoticePanel;
